import React, { useState } from "react";
import { Mail } from "lucide-react";
import api, { apiError } from "../api";
import { T, primaryBtn, inputStyle, labelStyle } from "../theme";
import { fontImport, responsiveCSS, LogoMark, PasswordField, LighthouseScene, VersionBadge } from "./shared";

export default function AuthScreen({ onAuthenticated, version, changelog }) {
  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("reset_token");
  const [mode, setMode] = useState(resetToken ? "reset" : "login"); // login | signup | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [devLink, setDevLink] = useState("");
  const [loading, setLoading] = useState(false);

  function switchMode(next) {
    setMode(next); setError(""); setInfo(""); setDevLink(""); setPassword(""); setConfirm("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setInfo(""); setDevLink("");
    const cleanEmail = email.trim().toLowerCase();
    setLoading(true);
    try {
      if (mode === "signup") {
        if (password.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
        if (password !== confirm) { setError("Le due password non coincidono."); return; }
        const { data } = await api.post("/auth/signup", { email: cleanEmail, password });
        onAuthenticated(data);
      } else if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: cleanEmail, password });
        onAuthenticated(data);
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: cleanEmail });
        setInfo(data.message);
        if (data.dev_reset_link) setDevLink(data.dev_reset_link);
      } else if (mode === "reset") {
        if (password.length < 8) { setError("La nuova password deve avere almeno 8 caratteri."); return; }
        if (password !== confirm) { setError("Le due password non coincidono."); return; }
        const { data } = await api.post("/auth/reset-password", { token: resetToken, new_password: password });
        window.history.replaceState({}, "", window.location.pathname);
        switchMode("login");
        setInfo(data.message);
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  const titles = { login: "Bentornato", signup: "Crea il tuo account", forgot: "Recupera la password", reset: "Nuova password" };
  const subtitles = {
    login: "Accedi al tuo account", signup: "Crea il tuo account gratuito",
    forgot: "Ti invieremo un link per reimpostare la password", reset: "Scegli la tua nuova password",
  };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, display: "flex", fontFamily: T.font, color: T.text }}>
      <style>{`${fontImport}${responsiveCSS} .auth-left { display: flex; } @media (max-width: 780px) { .auth-left { display: none !important; } }`}</style>
      <VersionBadge version={version} changelog={changelog} />

      <div className="auth-left" style={{ flex: 1.15, position: "relative", overflow: "hidden" }}>
        <LighthouseScene />
        <div style={{ position: "absolute", left: 48, bottom: 64, right: 48 }}>
          <div style={{ fontFamily: T.fontHead, fontSize: 40, fontWeight: 600, lineHeight: 1.12, color: T.text, maxWidth: 420, marginBottom: 14 }}>I tuoi social, con un aiuto vero.</div>
          <div style={{ fontSize: 15, color: T.textDim, maxWidth: 340 }}>So.Help ti dice cosa fare oggi per crescere sui social — un passo alla volta.</div>
        </div>
      </div>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
        <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 360 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 28 }}>
            <LogoMark size={26} />
            <span style={{ fontFamily: T.fontHead, fontSize: 22, fontWeight: 600 }}>So.Help</span>
          </div>
          <div style={{ fontFamily: T.fontHead, fontSize: 26, marginBottom: 6 }}>{titles[mode]}</div>
          <div style={{ fontSize: 13, color: T.textDim, marginBottom: 32 }}>{subtitles[mode]}</div>

          {mode !== "reset" && (
            <>
              <label style={labelStyle}>Email</label>
              <div style={{ position: "relative", marginBottom: 16 }}>
                <Mail size={14} color={T.textDim} style={{ position: "absolute", left: 10, top: 13 }} />
                <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  style={{ ...inputStyle, paddingLeft: 30, padding: "11px 12px 11px 30px" }} placeholder="nome@azienda.it" />
              </div>
            </>
          )}

          {mode !== "forgot" && (
            <>
              <label style={labelStyle}>{mode === "reset" ? "Nuova password" : "Password"}</label>
              <div style={{ marginBottom: mode === "login" ? 10 : 16 }}>
                <PasswordField testId="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
              </div>
            </>
          )}

          {mode === "login" && (
            <div style={{ textAlign: "right", marginBottom: 22 }}>
              <button data-testid="auth-forgot-link" type="button" onClick={() => switchMode("forgot")} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 12, padding: 0 }}>Password dimenticata?</button>
            </div>
          )}

          {(mode === "signup" || mode === "reset") && (
            <>
              <label style={labelStyle}>{mode === "reset" ? "Conferma nuova password" : "Conferma password"}</label>
              <div style={{ marginBottom: 22 }}>
                <PasswordField testId="auth-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
              </div>
            </>
          )}

          {info && <div data-testid="auth-info-message" style={{ color: T.teal, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{info}</div>}
          {devLink && (
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 16, background: T.surfaceAlt, border: `1px solid ${T.line}`, borderRadius: 8, padding: 10, lineHeight: 1.5 }}>
              Invio email non ancora configurato (modalità di prova): <a data-testid="auth-dev-reset-link" href={devLink} style={{ color: T.amber }}>apri il link di recupero</a>
            </div>
          )}
          {error && <div data-testid="auth-error-message" style={{ color: T.rose, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>{error}</div>}

          <button data-testid="auth-submit-btn" type="submit" disabled={loading} style={{ ...primaryBtn, width: "100%", padding: "12px 0", fontSize: 14, marginBottom: 18, opacity: loading ? 0.7 : 1 }}>
            {loading ? "Un momento…" : mode === "login" ? "Accedi" : mode === "signup" ? "Crea account" : mode === "forgot" ? "Invia il link di recupero" : "Aggiorna password"}
          </button>

          <div style={{ textAlign: "center", fontSize: 13, color: T.textDim }}>
            {mode === "login" && (
              <>Non hai un account?{" "}
                <button data-testid="auth-goto-signup" type="button" onClick={() => switchMode("signup")} style={{ background: "none", border: "none", color: T.amber, cursor: "pointer", fontSize: 13, padding: 0 }}>Creane uno</button>
              </>
            )}
            {(mode === "signup" || mode === "forgot" || mode === "reset") && (
              <>Hai già un account?{" "}
                <button data-testid="auth-goto-login" type="button" onClick={() => switchMode("login")} style={{ background: "none", border: "none", color: T.amber, cursor: "pointer", fontSize: 13, padding: 0 }}>Accedi</button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
